import { Loyalty } from '@lib/entities/Auth';
import { formatUSD, toCurrencyDecimal } from '@lib/types/currency';

/**
 * Order subtotal (in dollars) the user needs to reach to earn a reward.
 */
export const REWARD_NUDGE_TARGET = 12;

/**
 * The minimum subtotal (in dollars) at which we start nudging — roughly the
 * price of an entree or smoothie. Below this we stay quiet.
 */
export const REWARD_NUDGE_MIN_SUBTOTAL = 6.99;

/**
 * The nudge's spend figure: the subtotal after a REWARD discount, plus whether one was applied
 * (JSAD-6708).
 *
 * `invoice.subtotal` is pre-discount — `sumTotal` (lib/entities/Payment.ts) subtracts `discount`
 * from it separately, which is why the raw field is the wrong input here.
 *
 * Only a loyalty/Hang reward counts. `invoice.discount` is a single order-level field shared by
 * rewards, promo codes and credits, distinguished by `discount_type` — the same test
 * `getOrderSummary` uses to spot a Hang reward (Payment.ts:774). Promos and credits deliberately
 * do NOT arm the nudge, so their discount is not subtracted either: the figure the nudge reasons
 * about is "subtotal minus reward", and a promo leaves that untouched.
 *
 * `utensil_discount` is excluded for the same reason — a bag-fee credit is not a reward.
 *
 * Returns `hasRewardDiscount` alongside the net because the nudge REQUIRES a reward to have been
 * applied — see `getRewardSpendNudgeMessage`. Recomputed on every quote refresh, so applying or
 * removing a reward flips the nudge with no extra wiring.
 */
export const getPostDiscountSubtotal = (invoice?: {
    subtotal?: number;
    discount?: number;
    discount_type?: string;
}): { net: number; hasRewardDiscount: boolean } | undefined => {
    if (!invoice || typeof invoice.subtotal !== 'number' || Number.isNaN(invoice.subtotal)) {
        return undefined;
    }

    // `discount` is typed required on Invoice but real quote payloads can omit it (and it is
    // always absent on the first quote). Coercing to 0 matters: `subtotal - undefined` is NaN,
    // and NaN fails BOTH bounds checks in getRewardSpendNudgeMessage — so the nudge would render
    // with a "$NaN" message instead of staying hidden.
    const rawDiscount =
        typeof invoice.discount === 'number' && !Number.isNaN(invoice.discount)
            ? invoice.discount
            : 0;

    const hasRewardDiscount = invoice.discount_type === 'loyalty' && rawDiscount > 0;
    const rewardDiscount = hasRewardDiscount ? rawDiscount : 0;

    // Round to cents before comparing: float subtraction yields e.g. 6.989999999999999 for
    // 15 - 8.01, which would fall the wrong side of the $6.99 boundary. `toCurrencyDecimal`
    // only brands a plain number, it does not round, so do it explicitly.
    const net = Math.round((invoice.subtotal - rewardDiscount) * 100) / 100;

    // Never report a negative spend: a reward can meet or exceed the subtotal.
    return { net: Math.max(0, net), hasRewardDiscount };
};

/**
 * A placed order's spend toward the $12 reward threshold (JSAD-7193): the subtotal after every
 * discount, the same figure the backend reports to Hang as the order subtotal
 * (HangService.DoSendOrder). Tax, tip, fees and gift cards are excluded.
 */
export const getOrderRewardSpend = (order: {
    subtotal: number;
    discount: number;
    reward_applied_usd: number;
}): number => Math.round((order.subtotal - order.discount - order.reward_applied_usd) * 100) / 100;

/**
 * A user is enrolled in rewards if their loyalty profile has either a magic
 * link or a phone number. Absent both, they have no rewards account.
 *
 * `||` is deliberate. The backend creates the Hang account as a unit at account verification
 * and populates magic_link, phone, and membership_id together, so for a real account either
 * field implies the others — "at least one" and "both" are equivalent on real data. The
 * looser test is kept because it is the long-standing behavior and does not assume the
 * backend never ships a partially populated profile during a transition.
 *
 * `membership_id` is deliberately not consulted: it has never gated rewards access (its only
 * consumer is OneSignal identity in useAuth/usePushNotification), so requiring it would risk
 * locking out any legacy account that predates the field.
 */
export const isEnrolledInRewards = (loyalty?: Loyalty): boolean =>
    !!loyalty && (!!loyalty.magic_link || !!loyalty.phone);

/**
 * Returns the nudge message for the reward-spend nudge, or `undefined` when the
 * nudge should not be shown.
 *
 * Shown only when ALL hold:
 *   - the member is enrolled in rewards,
 *   - a REWARD discount is applied (`spend.hasRewardDiscount`), AND
 *   - the post-reward subtotal is $6.99–$11.99.
 *
 * The applied-reward requirement is the point: redeeming can drop an order under the $12 earning
 * threshold, and this warns the member before they commit. With no reward applied there is
 * nothing to warn about, so an undiscounted $8 order stays quiet — which is why landing on
 * checkout no longer shows the nudge unprompted.
 *
 * @param spend   Result of `getPostDiscountSubtotal(quote.invoice)` — the post-reward subtotal
 *                plus whether a reward produced it. Pass `undefined` before the quote loads.
 * @param loyalty The user's loyalty profile from profile-info
 */
export const getRewardSpendNudgeMessage = (
    spend: { net: number; hasRewardDiscount: boolean } | undefined,
    loyalty?: Loyalty
): string | undefined => {
    if (!isEnrolledInRewards(loyalty)) {
        return undefined;
    }

    if (!spend || !spend.hasRewardDiscount) {
        return undefined;
    }

    if (spend.net < REWARD_NUDGE_MIN_SUBTOTAL || spend.net >= REWARD_NUDGE_TARGET) {
        return undefined;
    }

    const remaining = formatUSD(toCurrencyDecimal(REWARD_NUDGE_TARGET - spend.net));

    return `You're $${remaining} away from earning a reward on this order!`;
};
